import * as React from 'react';
import { useSelector } from 'react-redux';
import Head from 'next/head';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import { ReduxState } from 'ducks';
import { selectBookingWidgetHeaderDescription } from 'lib/util/bookingWidgetDesign';
import { ProductDetails } from 'components/ProductDetails/ProductDetails';
import { FrequentlyViewedProducts } from 'components/FrequentlyViewedProducts/FrequentlyViewedProducts';
import { toProductShape } from 'lib/util/productShape';
import { getProductPageOgpTags } from 'lib/util/getProductPageOgpTags';
import { getProductStructuredReviewData } from 'lib/util/getProductStructuredReviewData';
import { getWeekdayText } from 'lib/util/getWeekdayText';
import { useCurrencyFormatter } from 'hooks/useCurrencyFormatter';

interface OwnProps {
  ogpPromotionId?: string;
  showOgpHighlight?: boolean;
}
type Props = OwnProps;

export const BookingWidgetProductDetails = ({ ogpPromotionId, showOgpHighlight }: Props) => {
  const { t, i18n } = useTranslation();

  const apiProduct = useSelector((state: ReduxState) => state.server.product.product);
  const headerText = useSelector(selectBookingWidgetHeaderDescription);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);
  const reviews = useSelector((state: ReduxState) => state.universal.reviews.all);

  const currencyFormatter = useCurrencyFormatter();

  if (!apiProduct) {
    return (
      <div>
        <Typography variant="h5" component="h3">
          {t('Product not found')}
        </Typography>
      </div>
    );
  }

  const product = toProductShape(apiProduct);

  const title = `${product.name || ''} | ${headerText}`;
  const description = product?.description || '';
  const dayOfWeek = apiProduct?.operating_days_of_week.length
    ? apiProduct.operating_days_of_week.map((weekday) => getWeekdayText(weekday, t)).join(',')
    : '';
  let lowestPrice = apiProduct?.lowest_price_gross || '';
  if (lowestPrice) {
    lowestPrice = currencyFormatter.format(lowestPrice);
  }

  let ogpTags;
  if (product) {
    ogpTags = getProductPageOgpTags(
      product,
      showOgpHighlight || false,
      ogpPromotionId || '',
      headerText,
      settings?.supplier_name || '',
      i18n.language || 'ja-JP',
      t,
      currencyFormatter
    );
  }

  const twitter = (settings?.sns_ids || []).find((snsId) => {
    return snsId?.key === 'twitter';
  });

  return (
    <>
      <Head>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        {ogpTags &&
          Object.entries(ogpTags).map(([key, value]) => (
            <meta key={key} property={key} content={value} />
          ))}
        {dayOfWeek && (
          <meta
            name="nutmeg:operating_days_of_week"
            content={t('Days of Week') + ':' + dayOfWeek}
          />
        )}
        {lowestPrice && <meta name="nutmeg:lowest_price_gross" content={lowestPrice} />}
        {twitter && twitter?.id && <meta name="twitter:site" content={`@${twitter.id}`} />}
        {apiProduct != null && reviews != null && (apiProduct?.review_count ?? 0) > 0 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(getProductStructuredReviewData(apiProduct, reviews)),
            }}
          />
        )}
      </Head>
      <ProductDetails />
      <FrequentlyViewedProducts />
    </>
  );
};
